import React, { useState } from "react";
import "./styles.scss";

interface FeatureItemProps {
  imgUrl: string;
  title: string;
  description: string;
  price: string;
  link?: string; // optional link for the feature item
  customButton?: React.ReactNode;
  delayMs?: number; // optional stagger animation delay
  isOnlineOrdering?: boolean; // if false, change button text
}

const FeatureitemComponent = ({
  imgUrl,
  title,
  description,
  price,
  customButton,
  delayMs = 0,
  link,
  isOnlineOrdering = true,
}: FeatureItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const orderLabel = isOnlineOrdering ? "Order Now" : "Order In Restaurant Only";
  const priceLabel = price || "";
  const longest = priceLabel && isOnlineOrdering
    ? (priceLabel.length > orderLabel.length ? priceLabel : orderLabel)
    : orderLabel;

  return (
    <section
      className="
        relative mx-auto max-w-3xl 
        rounded-[20px] 
        bg-[#2e2a2a] 
        border-2 border-white/20
        shadow-[15px_15px_30px_#1a1717,-15px_-15px_30px_#2f2b2b]
        transition-all duration-300 
        hover:shadow-[20px_20px_40px_#1a1717,-20px_-20px_40px_#2f2b2b] 
        active:shadow-[inset_10px_10px_20px_#1a1717,inset_-10px_-10px_20px_#2f2b2b]
        overflow-hidden
        featureitem
      "
      style={{ animationDelay: `${delayMs}ms` }}
    >
      {/* Hero image */}
      <div className="relative">
        <img
          src={imgUrl}
          alt={title}
          className="block h-[320px] w-full object-contain md:h-[400px] lg:h-[420px]"
        />

        {/* Title pill */}
  <div className="pointer-events-none absolute inset-x-6 -bottom-6 flex justify-center">
          <h3 className="pointer-events-auto inline-flex items-center rounded-full bg-black/80 px-6 py-3 text-center text-white text-xl font-extrabold tracking-wide uppercase shadow-lg ring-1 ring-white/10">
            {title}
          </h3>
        </div>
      </div>

      {/* Body */}
  <div className="px-6 pb-8 pt-14 md:px-10 md:pb-12">
        <p className="solway mx-auto max-w-2xl text-white text-base md:text-lg leading-relaxed uppercase tracking-wider text-left">
          {description}
        </p>

        <div className="mt-8 flex justify-center">
          {customButton ? (
            customButton
          ) : (
            <button
              type="button"
              className="relative inline-flex items-center justify-center rounded-xl bg-amber-400 px-8 py-4 text-base font-extrabold uppercase tracking-wide text-black shadow-md transition hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-300 active:scale-[0.99]"
              style={{ minWidth: `${longest.length}ch` }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onFocus={() => setIsHovered(true)}
              onBlur={() => setIsHovered(false)}
              aria-label={isHovered ? `Price ${priceLabel}` : orderLabel}
              onClick={() => {
                if (link) {
                  if (link.startsWith("http://") || link.startsWith("https://")) {
                    window.location.href = link;
                  } else {
                    // internal navigation (no router imported here, so fallback to location change)
                    window.location.href = link;
                  }
                }
              }}
            >
              <span
                className={`transition-opacity duration-150 ${
                  isOnlineOrdering && priceLabel && isHovered ? "opacity-0" : "opacity-100"
                }`}
              >
                {orderLabel}
              </span>
              {isOnlineOrdering && priceLabel && (
                <span
                  className={`absolute transition-opacity duration-150 ${
                    isHovered ? "opacity-100" : "opacity-0"
                  }`}
                >
                  {priceLabel}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureitemComponent;
